import React from "react";

import { JsxRenderer } from "../../ContentRenderer/JsxRenderer";

export type NodeInputProps = {
    data?: {
        label?: string;
        content?: React.ReactNode;
        getContent?: (data: any) => React.ReactNode;
        fillPercentage?: number;

        /**
         * @deprecated use `content` or `getContent` instead.
         */
        template?: string;
    }
};

export type ContentCallback = (data: any) => React.ReactNode;

/**
 * The chart's CURRENT content callbacks, one context each.
 *
 * They cannot be read from the element's own `data` alone.  A template writes them as inline
 * arrows, so every parent render hands the chart a new closure over new state, and the one baked
 * into `data` when the input was mapped is already stale -- a selection highlight or a live
 * reading would freeze at its first value.  Pushing the new arrow back into each node's and
 * edge's `data` did fix that, but `data` identity is what react-flow memoises on, so it rebuilt
 * and re-rendered the WHOLE chart -- every node, every edge, path maths included, plus a second
 * React pass -- once per parent render.  On a plant diagram driven by a slider popover that is a
 * full reconciliation per frame.
 *
 * Through context instead, nothing is rewritten and only the content itself re-renders, because a
 * context update reaches its consumers through the memo boundaries in between -- which is the
 * whole reason this works where a plain ref would not.  Two contexts rather than one object, so
 * that the value IS the callback: a chart passing a fresh `getNodeContent` every render then
 * leaves every link label untouched, where a shared object would have changed identity for both.
 * `data.getContent` stays as the fallback, for content rendered outside a provider and for
 * callers reading it directly.
 */
export const NodeContentContext = React.createContext<ContentCallback | undefined>(undefined);
export const EdgeContentContext = React.createContext<ContentCallback | undefined>(undefined);

const renderContent = (props: NodeInputProps, current?: ContentCallback) => {
    const { data, ...rest } = props;

    return (
        (current ?? data?.getContent)?.(data) ??
        (data?.template ? <JsxRenderer template={data?.template} data={{ ...rest, ...data }} /> : null) ??
        data?.content ??
        data?.label
    ) as React.ReactNode;
};

export function NodeContent(props: NodeInputProps) {
    return renderContent(props, React.useContext(NodeContentContext));
};

/** The same thing for a link: an edge label is content too, and takes `getEdgeContent`. */
export function EdgeContent(props: NodeInputProps) {
    return renderContent(props, React.useContext(EdgeContentContext));
};

export function VerticalFillDefinition({ fillPercentage, id }) {
    // Every shape renders this gradient unconditionally, but only references it when a
    // `fillPercentage` was supplied.  Without the fallback the two middle stops interpolate to
    // `offset="undefined%"`, which is invalid SVG and logs a browser console error for every
    // node on the chart.  The gradient is unreferenced in that case, so the value is arbitrary.
    const offset = fillPercentage ?? 0;

    return (
        <linearGradient id={id} x1="0" x2="0" y1="1.05" y2="-0.05">
            <stop offset="0%" stopColor="var(--fill-color)" />
            <stop offset={`${offset}%`} stopColor="var(--fill-color)" />
            <stop offset={`${offset}%`} stopColor="white" />
            <stop offset="100%" stopColor="white" />
        </linearGradient>
    );
}